import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import {
  format,
  startOfMonth,
  endOfMonth,
  subMonths,
  subQuarters,
  startOfYear,
  endOfYear,
  parseISO,
  isValid
} from 'date-fns';
import './RequiredCashReconciliationPage.css';

const SubwayRequiredCashPage = () => {
  const {  businessId } = useParams();

  const [plaidCategories, setPlaidCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [existingMatchId, setExistingMatchId] = useState(null);
  const [requiredCashByDate, setRequiredCashByDate] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  const [periodOption, setPeriodOption] = useState('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [selectedDates, setSelectedDates] = useState([]);

  const [selectedLinkedTxId, setSelectedLinkedTxId] = useState(null);
  const [expandedRCDate, setExpandedRCDate] = useState(null);
  const [rcLinkByDate, setRcLinkByDate] = useState({});

  const [expandedTxId, setExpandedTxId] = useState(null);
  const [txLinkById, setTxLinkById] = useState({});

  const [loadingRCDate, setLoadingRCDate] = useState({});
  const [loadingTxId, setLoadingTxId] = useState({});
  const [highlightedRCDates, setHighlightedRCDates] = useState(new Set());
  const [linkedSumByTx, setLinkedSumByTx] = useState({});

  const DISPLAY_FMT = 'MM/dd/yyyy';

  const fmtDate = (value, pattern = DISPLAY_FMT) => {
    if (!value) return '';
    const parsed =
      typeof value === 'string'
        ? parseISO(value)
        : value instanceof Date
          ? value
          : new Date(value);

    return isValid(parsed) ? format(parsed, pattern) : String(value);
  };

  const toNum = (x) =>
    typeof x === 'number' ? x : parseFloat(String(x).replace(/[,$]/g, '')) || 0;

  const toYMD = (d) => {
    const dt = d instanceof Date ? d : new Date(d);
    return dt.toISOString().slice(0, 10);
  };

  const formatCatName = (name = '') =>
    name
      .replace(/[_-]+/g, ' ')
      .trim()
      .split(/\s+/)
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ''))
      .join(' ');

  const computeDateRange = useCallback(() => {
    const today = new Date();
    let start_date;
    let end_date;

    switch (periodOption) {
      case 'previous-month':
        start_date = format(startOfMonth(subMonths(today, 1)), 'yyyy-MM-dd');
        end_date = format(endOfMonth(subMonths(today, 1)), 'yyyy-MM-dd');
        break;
      case 'previous-quarter':
        start_date = format(startOfMonth(subQuarters(today, 1)), 'yyyy-MM-dd');
        end_date = format(endOfMonth(subQuarters(today, 1)), 'yyyy-MM-dd');
        break;
      case 'year':
        start_date = format(startOfYear(today), 'yyyy-MM-dd');
        end_date = format(endOfYear(today), 'yyyy-MM-dd');
        break;
      case 'custom':
        start_date = customStart;
        end_date = customEnd;
        break;
      default:
        start_date = format(startOfMonth(today), 'yyyy-MM-dd');
        end_date = format(endOfMonth(today), 'yyyy-MM-dd');
        break;
    }

    return { start_date, end_date };
  }, [periodOption, customStart, customEnd]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/categories', {
        params: { business_id: businessId }
      });
      setPlaidCategories(res.data || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, [businessId]);

  const fetchTransactions = useCallback(async (categoryId) => {
    try {
      const res = await api.get('/plaid-transactions', {
        params: { business_id: businessId, category_id: categoryId }
      });

      const txs = res.data || [];
      setTransactions(txs);

      const summaries = {};
      await Promise.all(
        txs.map(async (t) => {
          try {
            const { data } = await api.get('/subway-required-cash-link-for-tx', {
              params: { business_id: businessId, plaid_id: t.id }
            });

            const total = Array.isArray(data?.rc_rows)
              ? data.rc_rows.reduce((sum, r) => sum + toNum(r.amount), 0)
              : 0;

            summaries[t.id] = total;
          } catch {
            summaries[t.id] = 0;
          }
        })
      );

      setLinkedSumByTx(summaries);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
      setTransactions([]);
      setLinkedSumByTx({});
    }
  }, [businessId]);

  const fetchCategoryMatch = useCallback(async () => {
    try {
      const res = await api.get('/subway-required-cash-category-match', {
        params: { business_id: businessId, cash_category_name: 'Subway Required Cash' }
      });

      if (res.data && res.data.plaid_category_id) {
        setSelectedCategoryId(String(res.data.plaid_category_id));
        setExistingMatchId(res.data.id);
        fetchTransactions(res.data.plaid_category_id);
      }
    } catch (err) {
      console.error('Failed to fetch Subway category match:', err);
    }
  }, [businessId, fetchTransactions]);

  const fetchRequiredCash = useCallback(async () => {
    try {
      const { start_date, end_date } = computeDateRange();

      const { data } = await api.get('/subway-required-cash-by-date', {
        params: {
          business_id: businessId,
          start_date,
          end_date
        }
      });

      setRequiredCashByDate(
        (data || []).map((r) => ({
          date: r.date,
          cashTenderTotal: Number(r.cash_tender_total || 0),
          subwayPaidoutsTotal: Number(r.subway_paidouts_total || 0),
          requiredCash: Number(r.required_cash || 0),
          reconciled: !!r.rc_reconciled,
          bank_transaction_id: r.bank_transaction_id || null
        }))
      );
    } catch (err) {
      console.error('Failed to fetch Subway required cash:', err);
    }
  }, [businessId, computeDateRange]);

  const findTxByBankId = (bankId) => {
    if (bankId == null) return null;
    const id = String(bankId);

    return (
      transactions.find((t) => String(t.id) === id) ||
      transactions.find((t) => String(t.pending_transaction_id || '') === id) ||
      null
    );
  };

  useEffect(() => {
    fetchCategories();
    fetchCategoryMatch();
    fetchRequiredCash();
  }, [fetchCategories, fetchCategoryMatch, fetchRequiredCash]);

  const handleSaveMatch = async () => {
    try {
      if (existingMatchId) {
        await api.put(`/subway-required-cash-category-match/${existingMatchId}`, {
          business_id: businessId,
          selected_category_id: selectedCategoryId
        });
      } else {
        const res = await api.post('/subway-required-cash-category-match', {
          business_id: businessId,
          selected_category_id: selectedCategoryId
        });
        setExistingMatchId(res.data.id);
      }

      setIsEditing(false);
      fetchTransactions(selectedCategoryId);
      alert('Category match saved successfully!');
    } catch (err) {
      console.error('Failed to save Subway category match:', err);
      alert('Failed to save match.');
    }
  };

  const handleDateSelection = (date) => {
    setSelectedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]
    );
  };

  const selectedDatesTotal = useMemo(() => {
    if (!selectedDates.length) return 0;
    const map = new Map(requiredCashByDate.map((r) => [r.date, Number(r.requiredCash || 0)]));
    return selectedDates.reduce((sum, d) => sum + (map.get(d) || 0), 0);
  }, [selectedDates, requiredCashByDate]);

  const toggleExpandRC = async (row) => {
  console.log("CLICKED ROW:", row);

  if (!row?.reconciled) return;

  const rowDate = format(parseISO(row.date), 'yyyy-MM-dd');

  const isSame = expandedRCDate === rowDate;
  const next = isSame ? null : rowDate;

  setExpandedRCDate(next);
  if (next) {
  setHighlightedRCDates(new Set([next]));
} else {
  setHighlightedRCDates(new Set());
}

  if (!next) return;

  if (rcLinkByDate[next]) {
    const plaid = rcLinkByDate[next]?.plaid;

    if (plaid) {
      const tx =
        findTxByBankId(plaid.id) ||
        findTxByBankId(plaid.pending_transaction_id);

      setSelectedLinkedTxId(tx ? tx.id : null);
    } else {
      setSelectedLinkedTxId(null);
    }

    return;
  }

  try {
    setLoadingRCDate((prev) => ({ ...prev, [next]: true }));

    const { data } = await api.get('/subway-required-cash-link-for-date', {
      params: { business_id: businessId, date: next }
    });

    setRcLinkByDate((prev) => ({ ...prev, [next]: data || {} }));

    const plaid = data?.plaid;

    if (plaid) {
      const tx =
        findTxByBankId(plaid.id) ||
        findTxByBankId(plaid.pending_transaction_id);

      setSelectedLinkedTxId(tx ? tx.id : null);
    } else {
      setSelectedLinkedTxId(null);
    }
  } finally {
    setLoadingRCDate((prev) => {
      const { [next]: _, ...rest } = prev;
      return rest;
    });
  }
};

  const toggleExpandTx = async (tx) => {
    const isSame = expandedTxId === tx.id;
    const next = isSame ? null : tx.id;
    setExpandedTxId(next);

    if (!next) {
      setHighlightedRCDates(new Set());
      return;
    }

    if (txLinkById[next]) {
  const cachedDates = (txLinkById[next].rc_rows || []).map((r) =>
    format(parseISO(r.entry_date), 'yyyy-MM-dd')
  );

  setHighlightedRCDates(new Set(cachedDates));
  setSelectedLinkedTxId(tx.id);
  return;
}

    try {
      setLoadingTxId((prev) => ({ ...prev, [next]: true }));

      const { data } = await api.get('/subway-required-cash-link-for-tx', {
        params: { business_id: businessId, plaid_id: next }
      });

      setTxLinkById((prev) => ({ ...prev, [next]: data || {} }));

      const dates = (data?.rc_rows || []).map(r =>
  format(parseISO(r.entry_date), 'yyyy-MM-dd')
);
      setHighlightedRCDates(new Set(dates));
      setSelectedLinkedTxId(tx.id);
    } catch (err) {
      console.error('toggleExpandTx error:', err);
      setHighlightedRCDates(new Set());
    } finally {
      setLoadingTxId((prev) => {
        const { [next]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const sumLinkedForTx = (tx) => {
    if (!tx) return 0;
    const tid = String(tx.id);
    const pid = String(tx.pending_transaction_id || '');

    return requiredCashByDate
      .filter(
        (r) =>
          String(r.bank_transaction_id || '') === tid ||
          (pid && String(r.bank_transaction_id || '') === pid)
      )
      .reduce((sum, r) => sum + Number(r.requiredCash || 0), 0);
  };

  const matchSelectedDatesToTx = async (tx) => {
    if (!selectedDates.length) {
      alert('Select one or more dates on the left first.');
      return;
    }

    const totalRequiredCash = selectedDates.reduce((sum, date) => {
      const rc = requiredCashByDate.find((r) => r.date === date)?.requiredCash || 0;
      return sum + Number(rc || 0);
    }, 0);

    const txAmount = Number(tx.amount);
    const diff = totalRequiredCash + txAmount;

    const statusMsg =
      diff === 0
        ? 'Perfect Match'
        : diff > 0
          ? `Short by $${diff.toFixed(2)}`
          : `Over by $${Math.abs(diff).toFixed(2)}`;

    const ok = window.confirm(
      `Selected Dates Total: $${totalRequiredCash.toFixed(2)}\n` +
      `Transaction Amount:  $${txAmount.toFixed(2)}\n` +
      `${statusMsg}\n\nProceed to reconcile?`
    );

    if (!ok) return;

    try {
      const normDates = selectedDates.map(toYMD);

      await api.post('/subway-required-cash-match', {
        business_id: businessId,
        plaid_transaction_id: tx.id,
        matched_dates: normDates,
        total_required_cash: totalRequiredCash,
        plaid_amount: txAmount,
        difference: diff
      });

      await fetchRequiredCash();
      await fetchTransactions(selectedCategoryId);

      setHighlightedRCDates(new Set(normDates));
      setSelectedLinkedTxId(tx.id);
      setExpandedTxId(tx.id);

      setTxLinkById((prev) => {
        const { [tx.id]: _stale, ...rest } = prev;
        return rest;
      });

      const freshTxLink = await api.get('/subway-required-cash-link-for-tx', {
        params: { business_id: businessId, plaid_id: tx.id }
      });

      setTxLinkById((prev) => ({ ...prev, [tx.id]: freshTxLink.data || {} }));

      if (expandedRCDate && selectedDates.includes(expandedRCDate)) {
        const freshRCLink = await api.get('/subway-required-cash-link-for-date', {
          params: { business_id: businessId, date: expandedRCDate }
        });

        setRcLinkByDate((prev) => ({
          ...prev,
          [expandedRCDate]: freshRCLink.data || {}
        }));
      }

      setSelectedDates([]);
      alert('Reconciled.');
    } catch (err) {
      console.error('matchSelectedDatesToTx error', err);
      alert('Failed to reconcile.');
    }
  };

  const unlinkRcFromTx = async (tx, rcRow) => {
    const ok = window.confirm(
      `Unlink ${rcRow.entry_date} — $${Number(rcRow.amount || 0).toFixed(2)} from this transaction?`
    );

    if (!ok) return;

    try {
      await api.post('/subway-required-cash-unlink', {
        business_id: businessId,
        rc_link_id: rcRow.id
      });

      await fetchRequiredCash();
      await fetchTransactions(selectedCategoryId);

      setSelectedLinkedTxId(tx.id);
      setExpandedTxId(tx.id);

      setTxLinkById((prev) => {
        const { [tx.id]: _stale, ...rest } = prev;
        return rest;
      });

      const freshLink = await api.get('/subway-required-cash-link-for-tx', {
        params: { business_id: businessId, plaid_id: tx.id }
      });

      setTxLinkById((prev) => ({ ...prev, [tx.id]: freshLink.data || {} }));

      const freshDates = (freshLink.data?.rc_rows || []).map((r) => r.entry_date);
      setHighlightedRCDates(new Set(freshDates));
    } catch (err) {
      console.error('unlinkRcFromTx error', err);
      alert('Failed to unlink.');
    }
  };

  const handleCategoryChange = (e) => {
    setSelectedCategoryId(e.target.value);
  };

  const handlePeriodChange = (e) => {
    setPeriodOption(e.target.value);
  };

  const handleCustomDateChange = (e) => {
    if (e.target.name === 'customStart') setCustomStart(e.target.value);
    else setCustomEnd(e.target.value);
  };

  return (
    <div className='req-cash-page'>
      <h2 className='pw-page-title'>Subway Required Cash Reconciliation</h2>

      

      <div className='req-cash-cat'>
        <div className='cat-match-req'>
          <label style={{ marginRight: '10px', alignContent: 'center' }}>Period: </label>
          <select value={periodOption} onChange={handlePeriodChange}>
            <option value='month'>Current Month</option>
            <option value='previous-month'>Previous Month</option>
            <option value='previous-quarter'>Previous Quarter</option>
            <option value='year'>Current Year</option>
            <option value='custom'>Custom</option>
          </select>

          {periodOption === 'custom' && (
            <div className='start-div'>
              <label style={{ marginLeft: '15px' }}>Start Date: </label>
              <input
                type='date'
                name='customStart'
                value={customStart}
                onChange={handleCustomDateChange}
              />
              <label style={{ marginLeft: '10px' }}>End Date: </label>
              <input
                type='date'
                name='customEnd'
                value={customEnd}
                onChange={handleCustomDateChange}
              />
              <button style={{ marginLeft: '10px' }} onClick={fetchRequiredCash}>
                Apply
              </button>
            </div>
          )}
        </div>

        <div className='cat-match-req'>
          {existingMatchId && !isEditing ? (
           <div className="matched-inline">
  <span className="matched-inline-label">Matched Category:</span>
  <span className="matched-inline-value">
    {formatCatName(
      plaidCategories.find((c) => c.id === Number(selectedCategoryId))?.name || ''
    )}
  </span>

  <button
    className="matched-inline-btn"
    onClick={() => setIsEditing(true)}
  >
    Edit
  </button>
</div>
          ) : (
            <div  className='matched-sec'>
              <label >Select Category to Match: </label>
              <select value={selectedCategoryId} onChange={handleCategoryChange}>
                <option value=''>-- Select Category --</option>
                {plaidCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {formatCatName(cat.name)}
                  </option>
                ))}
              </select>
              <button
                onClick={handleSaveMatch}
                disabled={!selectedCategoryId}
              >
                Save
              </button>
            </div>
          )}
        </div>
      </div>

      <div className='req-cash-tables'>
        <div className='req-cash-entry'>
          <h3>Subway Required Cash by Date</h3>

          <table className='rc-table'>
            <thead>
              <tr>
                <th></th>
                <th>Date</th>
                <th>Cash Tender</th>
                <th>Subway Paidouts</th>
                <th>Required Cash</th>
              </tr>
            </thead>
            <tbody>
              {requiredCashByDate.map((row) => {
  const rowDate = format(parseISO(row.date), 'yyyy-MM-dd');

  const isExpanded = expandedRCDate === rowDate;
  const link = rcLinkByDate[rowDate];
  const isHighlighted = highlightedRCDates.has(rowDate);

                return (
                  <React.Fragment key={row.date}>
                    <tr
                      onClick={() => toggleExpandRC(row)}
                      className={isHighlighted ? 'rc-row--highlight' : ''}
                      style={{ cursor: row.reconciled ? 'pointer' : 'default' }}
                      title={
                        row.reconciled && row.bank_transaction_id
                          ? `Linked to ${row.bank_transaction_id}`
                          : ''
                      }
                    >
                      <td>
                        <input
                          type='checkbox'
                          checked={selectedDates.includes(row.date)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleDateSelection(row.date);
                          }}
                        />
                      </td>
                      <td>
                        {fmtDate(row.date)}{' '}
                        {row.reconciled && (
                          <span style={{ color: 'green', fontWeight: 700 }}>✅</span>
                        )}
                      </td>
                      <td>${Number(row.cashTenderTotal || 0).toFixed(2)}</td>
                      <td>${Number(row.subwayPaidoutsTotal || 0).toFixed(2)}</td>
                      <td>
                        <strong>${Number(row.requiredCash || 0).toFixed(2)}</strong>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr>
                        <td colSpan={5} style={{ background: '#fafafa' }}>
                          {loadingRCDate[row.date] && <div>Loading linked transaction…</div>}

                          {!loadingRCDate[row.date] && (!link || !link.plaid) && (
                            <div style={{ color: '#a00' }}>
                              No linked Plaid transaction found.
                            </div>
                          )}

                          {!loadingRCDate[row.date] && link?.plaid && (
                            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                              <div>
                                <strong>Linked Tx:</strong> {link.plaid.name}
                              </div>
                              <div>
                                <strong>Date:</strong> {fmtDate(link.plaid.date)}
                              </div>
                              <div>
                                <strong>Amount:</strong> $
{Math.abs(Number(link.plaid.amount || 0)).toFixed(2)}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {selectedDates.length > 0 && (
            <div className='rc-selection-summary'>
              <div className='rc-selection-summary__left'>
                <strong>{selectedDates.length}</strong>{' '}
                {selectedDates.length === 1 ? 'date' : 'dates'} selected
              </div>
              <div className='rc-selection-summary__right'>
                <span>Selected Total:</span>
                <strong>${selectedDatesTotal.toFixed(2)}</strong>
                <button
                  className='rc-clear-btn'
                  onClick={() => setSelectedDates([])}
                  aria-label='Clear selected dates'
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>

        <div className='req-cash-plaid'>
          <h3>Matched Transactions</h3>

          {transactions.length > 0 ? (
            <table className='rc-table'>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Difference</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => {
                  const isLinked = selectedLinkedTxId && tx.id === selectedLinkedTxId;
                  const isExpanded = expandedTxId === tx.id;
                  const link = txLinkById[tx.id];

                  return (
                    <React.Fragment key={tx.id}>
                      <tr
                        data-tx-id={tx.id}
                        onClick={() => toggleExpandTx(tx)}
                        className={isLinked ? 'tx-row--linked' : ''}
                        style={{ cursor: 'pointer' }}
                        title={isLinked ? 'Linked to selected reconciled date' : ''}
                      >
                        <td>{fmtDate(tx.date)}</td>
                        <td>
                          {tx.name}
                          {Number(linkedSumByTx[tx.id] || 0) > 0 && (
                            <span style={{ marginLeft: 6, color: 'green', fontWeight: 700 }}>
                              ✅
                            </span>
                          )}
                        </td>
                        <td>${Math.abs(parseFloat(tx.amount)).toFixed(2)}</td>
                        <td>
                          {(() => {
                            const linked = linkedSumByTx[tx.id] ?? sumLinkedForTx(tx);
                            const remaining = -(toNum(linked) + toNum(tx.amount));
                            const isZero = Math.abs(remaining) < 0.005;
                            const badgeClass = isZero ? 'ok' : remaining < 0 ? 'err' : 'ok';
                            const sign = remaining < 0 ? '-' : '+';
                            const display = isZero
                              ? '$0.00'
                              : `${sign}$${Math.abs(remaining).toFixed(2)}`;

                            return <span className={`delta-badge ${badgeClass}`}>{display}</span>;
                          })()}
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td colSpan={4} className='is-detail'>
                            {loadingTxId[tx.id] ? (
                              <div>Loading linked Subway Required Cash…</div>
                            ) : (
                              <>
                                {link && link.rc_rows?.length ? (
                                  <div>
                                    <div style={{ marginBottom: 6 }}>
                                      <strong>Linked Subway Required Cash dates:</strong>
                                    </div>

                                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                                      {link.rc_rows.map((r) => (
                                        <li
                                          key={r.entry_date}
                                          
                                        >
                                          <span>
                                            {fmtDate(r.entry_date)} — $
                                            {Number(r.amount || 0).toFixed(2)}
                                          </span>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              unlinkRcFromTx(tx, r);
                                            }}
                                            title='Unlink'
                                            style={{
                                              border: 'none',
                                              background: 'transparent',
                                              cursor: 'pointer',
                                              color: '#b00',
                                              fontSize: 16,
                                              lineHeight: 1
                                            }}
                                            aria-label='Unlink'
                                          >
                                            🗑️
                                          </button>
                                        </li>
                                      ))}
                                    </ul>

                                    <div style={{ marginTop: 6, fontWeight: 600}}>
                                      <strong>Total:</strong>{' '}
                                      $
                                      {link.rc_rows
                                        .reduce((sum, r) => sum + Number(r.amount || 0), 0)
                                        .toFixed(2)}
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ color: '#a00' }}>
                                    No Subway Required Cash rows linked.
                                  </div>
                                )}

                                <div style={{ marginTop: 8 }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      matchSelectedDatesToTx(tx);
                                    }}
                                  >
                                    Match selected dates to this transaction
                                  </button>
                                </div>
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p>No transactions found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubwayRequiredCashPage;